import { USER_ROLES } from "@nlc/shared-types";
import { getRouteApi } from "@tanstack/react-router";

import type { UserRole } from "@/features/auth/permissions/config";

import { FeaturePageLayout } from "@/features/shared/layout";

import { ChecksTable } from "./checks/ChecksTable";
import { useChecks } from "./checks/hooks/useChecks";
import { useAssignments } from "./design-types/hooks/useAssignments";
import { GuidelinesTable } from "./guidelines/GuidelinesTable";
import { useGuidelines } from "./guidelines/hooks/useGuidelines";

const route = getRouteApi("/_auth/_app-shell/admin/mbc/");

const SUPER_ADMIN_ONLY = [USER_ROLES.SUPER_ADMIN] as UserRole[];

const TABS = [
  { id: "guidelines", label: "Guidelines" },
  { id: "checks", label: "Checks" },
];

const CREATE_BUTTONS = {
  checks: {
    href: "/admin/mbc/checks/new",
    label: "New check",
    requiredRoles: SUPER_ADMIN_ONLY,
  },
  guidelines: {
    href: "/admin/mbc/guidelines/new",
    label: "New guideline",
    requiredRoles: SUPER_ADMIN_ONLY,
  },
};

export function MbcAdminPage() {
  const { tab } = route.useSearch();
  const navigate = route.useNavigate();
  const checks = useChecks();
  const guidelines = useGuidelines();
  const assignments = useAssignments();
  const queries = [checks, guidelines, assignments];

  const isLoading = queries.some((query) => query.isLoading);
  const error = queries.find((query) => query.error)?.error;

  return (
    <div className="p-xl">
      <FeaturePageLayout
        createButton={CREATE_BUTTONS[tab]}
        isLoading={isLoading}
        tabs={{
          onSelectionChange: (key) =>
            navigate({ search: { tab: key as typeof tab } }),
          selectedKey: tab,
          tabs: TABS,
        }}
        title="MBC Settings"
      >
        <div className="flex w-full flex-col gap-6 p-6">
          {error && (
            <p className="text-sm text-text-error" role="alert">
              Could not load the MBC settings: {error.message}
            </p>
          )}

          {checks.data &&
            guidelines.data &&
            assignments.data &&
            (tab === "checks" ? (
              <ChecksTable checks={checks.data} />
            ) : (
              <GuidelinesTable
                defaultGuidelineId={assignments.data.default.guidelineId}
                guidelines={guidelines.data}
              />
            ))}
        </div>
      </FeaturePageLayout>
    </div>
  );
}
